import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import Icon from '../../../../components/icons'
import Text from '../../../../elements/Typo/Text'

import { ExperimentDetailsI } from '../../../../_config/types'
import { trackEvent } from '../../../../_common/functions'
import ShareExperimentModal from '../../../../components/modal/ShareExperiment.modal'
import ExperimentReviewModal from '../../../CreateExperiment/ConfigureGroups/_components/modal/experimentReview.modal'
import ScheduleasRecurringMeasurementModal from '../../../../components/modal/ScheduleasRecurringMeasurement.modal'

interface Props {
    experiment: ExperimentDetailsI
    isEnabledReviewNote: boolean
    isEnabledViewNote: boolean
    openComment: () => void
    experimentStatusDetails: string
    viewDetails: () => void
}
const MoreActions: React.FC<Props> = ({ experiment, isEnabledReviewNote, isEnabledViewNote, openComment, experimentStatusDetails, viewDetails }) => {
    const { t } = useTranslation()
    const experimentShareModal: any = useRef()
    const experimentDetailsViewModal: any = useRef()

    const dropdownRef = useRef<HTMLDivElement>(null)
    const [showDropdown, setShowDropdown] = useState<boolean>(false)
    const [recurringModal, setRecurringModal] = useState<boolean>(false)

    let showRecurringButton: boolean = false
    if (
        !experiment.is_measurement_flow &&
        experiment.is_sdk != 1 &&
        experiment?.data_mappings?.delta_design_table_name != null &&
        experiment?.data_mappings?.delta_measurement_table_name != null &&
        (experiment.parent_experiment_id == null || experiment.parent_experiment_id == 0)
    ) {
        showRecurringButton = true
    }

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const _handleShowShareDetails = () => {
        experimentShareModal.current?.showModal()
    }
    const _handleShowExperimentDetails = () => {
        experimentDetailsViewModal.current?.showModal()
    }

    return (
        <div ref={dropdownRef} className="relative">
            <button
                type="button"
                onClick={() => {
                    trackEvent('experiment_details_more_actions_opened', { experiment_literal: experiment?.experiment_literal })
                    setShowDropdown(true)
                }}
                className="flex gap-[0.33rem] bg-[#FFF] rounded-[1.667rem] items-center"
                data-aos="zoom-in"
                data-aos-delay="200"
                data-testid="dbtn"
            >
                <div data-testid="share-button" aria-label={t('More Actions')} className="h-[1.182rem] w-[1.182rem] cursor-pointer">
                    <Icon name="more-actions" size={0.682}></Icon>
                </div>
            </button>
            {showDropdown && (
                <div className="absolute bg-white border px-2 rounded-lg shadow-1 w-32 z-10 py-1" style={{ right: -4 }}>
                    <div className="border-b-0.5 border-b border-slate-300 flex py-1">
                        {isEnabledReviewNote && (
                            <button
                                type="button"
                                onClick={() => {
                                    trackEvent('experiment_details_more_actions_review', { experiment_literal: experiment?.experiment_literal })
                                    openComment()
                                }}
                                className="flex gap-[0.33rem] bg-[#FFF] items-center"
                            >
                                <Text size="xs" className="font-normal !text-[0.545rem]">
                                    {t('Review')}
                                </Text>
                            </button>
                        )}
                        {isEnabledViewNote && (
                            <button
                                type="button"
                                onClick={() => {
                                    trackEvent('experiment_details_more_actions_view_note', { experiment_literal: experiment?.experiment_literal })
                                    openComment()
                                }}
                                className="flex gap-[0.33rem] bg-[#FFF] items-center"
                            >
                                <Text size="xs" className="font-normal !text-[0.545rem]">
                                    {t('View Note')}
                                </Text>
                            </button>
                        )}
                    </div>
                    {showRecurringButton && (
                        <div className="border-b-0.5 border-b border-slate-300 flex py-1">
                            <button
                                type="button"
                                onClick={() => {
                                    trackEvent('experiment_details_more_actions_recurring', { experiment_literal: experiment?.experiment_literal })
                                    setRecurringModal(true)
                                }}
                                className="flex gap-[0.33rem] bg-[#FFF] items-center"
                            >
                                <Text size="xs" className="font-normal !text-[0.545rem]">
                                    {t('Recurring Measurement')}
                                </Text>
                            </button>
                        </div>
                    )}

                    <div className="border-b-0.5 border-b border-slate-300 flex py-1">
                        <button
                            type="button"
                            onClick={() => {
                                trackEvent('experiment_details_more_actions_shared_with', { experiment_literal: experiment?.experiment_literal })
                                trackEvent('share_experiment_modal_opened', { experiment_literal: experiment?.experiment_literal, source: 'details_more_actions' })
                                _handleShowShareDetails()
                            }}
                            className="flex gap-[0.33rem] bg-[#FFF] items-center"
                        >
                            <Text size="xs" className="font-normal !text-[0.545rem]">
                                {t('Shared with')}
                            </Text>
                        </button>
                    </div>
                    <div className="border-b-0.5 border-b border-slate-300 flex py-1">
                        <button
                            type="button"
                            onClick={() => {
                                trackEvent('experiment_details_more_actions_status_details', { experiment_literal: experiment?.experiment_literal })
                                viewDetails()
                            }}
                        >
                            <Text size="13" className="font-normal !text-[0.545rem] text-start">
                                {t('{{status}} Details', { status: experimentStatusDetails })}
                            </Text>
                        </button>
                    </div>
                    <div className="flex py-1">
                        <button
                            type="button"
                            onClick={() => {
                                trackEvent('experiment_details_more_actions_more_info', { experiment_literal: experiment?.experiment_literal })
                                _handleShowExperimentDetails()
                            }}
                        >
                            <Text size="13" className="font-normal !text-[0.545rem]">
                                {t('More Info')}
                            </Text>
                        </button>
                    </div>
                </div>
            )}
            {recurringModal && (
                <ScheduleasRecurringMeasurementModal
                    isModalOpen={recurringModal}
                    onClose={() => {
                        setRecurringModal(false)
                    }}
                    experiment={experiment}
                />
            )}
            {experiment?.experiment_literal && showDropdown && <ShareExperimentModal _ref={experimentShareModal} experiment={experiment} updateSharedUserList={() => {}} />}
            <ExperimentReviewModal _ref={experimentDetailsViewModal} success={() => {}} viewOnly={true} />
        </div>
    )
}
export default MoreActions
